import axios from 'axios';
import { parse } from 'cookie';
import { RAT_PROJECT_KEY, VERSION } from '../../config';
import { WebBlocksApiBlockT } from '../REST/types';

type UachType =
	| {
			uach_arch: string;
			uach_bitness: string;
			uach_full_version_list: string;
			uach_platform_version: string;
			uach_wow64: string;
			uach_model: string;
	  }
	| {};

export type TrackResponseType = {
	acc: number | null;
	aid: number | null;
	etype: string;
	compid: string[];
	app_ver: string;
	pgl: string;
	cp: {
		campaign_id: string;
		content_id: string;
		message: string;
		service_acc: number;
		service_id: number;
		service_type: string;
	};
	url: string;
	ua: string;
	_ra: string;
	cks: string;
	cks2: string;
	use_cks: boolean;
	pgn: string;
	pgid: string;
} & UachType;

export type ApiResponseType = {
	isFetching: boolean;
	error?: Error;
	result?: WebBlocksApiBlockT[];
};

const PC = 'PC';
const SP = 'SP';
type PglTypes = typeof PC | typeof SP;

function isRATAvailable(): boolean {
	return (
		typeof window !== 'undefined' &&
		!!window.RAT &&
		typeof window.RAT.addCustomEvent === 'function'
	);
}

async function getUach(): Promise<UachType> {
	const { userAgentData } = window.navigator;

	if (!userAgentData || !userAgentData?.getHighEntropyValues) {
		return {};
	}

	const { architecture, bitness, fullVersionList, mobile, platformVersion } =
		await userAgentData.getHighEntropyValues([
			'architecture',
			'bitness',
			'platformVersion',
			'fullVersionList',
		]);

	const fullVersionListStr = fullVersionList
		?.map((v) => `"${v.brand}";v="${v.version}"`)
		.join(', ');

	return {
		uach_arch: architecture,
		uach_bitness: bitness,
		uach_full_version_list: fullVersionListStr,
		uach_platform_version: platformVersion,
		uach_wow64: `${mobile}`,
	};
}

function getRatData(
	screen: string = 'EMPTY',
	action: string = 'EMPTY',
	campaignId: string = '',
	contentId: string = '',
	ratAccountId: string = '',
	ratServiceType: string = '',
	message: any = null,
	pgl: PglTypes = PC,
	uach: UachType = {},
	pgid: string = ''
): TrackResponseType {
	const { Ra, Rp } = parse(document.cookie);

	return {
		acc: +ratAccountId,
		aid: 1,
		etype: 'async',
		compid: [screen, action],
		app_ver: VERSION,
		pgl,
		cp: {
			campaign_id: campaignId,
			content_id: contentId,
			message: JSON.stringify(message),
			service_acc: +ratAccountId,
			service_id: 1,
			service_type: ratServiceType,
		},
		url: window.location.href,
		ua: navigator.userAgent,
		_ra: Ra,
		cks: Rp,
		cks2: Rp,
		use_cks: true,
		pgn: window.location.pathname,
		pgid,
		...uach,
	};
}

async function postEventToRAT(data = {}) {
	const apiURL = 'https://rat.rakuten.co.jp';
	const dataString = `cpkg_none=[${JSON.stringify(data)}]`;
	const jsonResponse = await axios.post(apiURL, dataString, {
		headers: { 'Content-Type': 'text/plain' },
	});

	return jsonResponse.data;
}

export async function Track({
	screen = 'EMPTY',
	action = 'EMPTY',
	ratAccountId = '',
	ratServiceType = '',
	campaignId = undefined,
	contentId = undefined,
	message = null,
	isMobile = false,
}: {
	screen: string;
	action: string;
	ratAccountId: string;
	ratServiceType: string;
	campaignId?: string;
	contentId?: string;
	message?: any;
	isMobile: boolean;
}): Promise<TrackResponseType | null> {
	try {
		const projectKey = RAT_PROJECT_KEY;
		const screenProductPrefix = [projectKey, screen].join('_');
		const type = isMobile ? SP : PC;
		const pgid = window.RAL?.c?.prototype?.sc
			? window.RAL?.c?.prototype?.sc()
			: '';
		const uachData = await getUach();

		const ratData = getRatData(
			screenProductPrefix,
			action,
			campaignId,
			contentId,
			ratAccountId,
			ratServiceType,
			message,
			type,
			uachData,
			pgid
		);

		// NOTE: If rat is not available on the page - no call API
		if (isRATAvailable()) {
			window.RAL?.eventLog?.push(ratData);
			await postEventToRAT(ratData);
		} else {
			// eslint-disable-next-line no-console
			console.log(`[RAT-SIMULATOR] ${JSON.stringify(ratData.compid)}`, ratData);
		}

		return ratData;
	} catch (e) {
		return null;
	}
}
